import type { VirtualDevice, SimulationContext } from './VirtualDevice';
// eslint-disable-next-line no-restricted-imports
import type { RuntimeHost } from '@/plc/runtime/RuntimeHost';
import type { AlarmManager } from '../alarms/AlarmManager';
import { DEVICE_FACTORIES } from './devices';
// eslint-disable-next-line no-restricted-imports
import type { Device } from '@/devices/types';
import type { PipeConnection } from '@/project/types';

export interface Product {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  active: boolean;
  conveyorId?: string;
  velocity: number;
  blockedTimeMs: number;
  blocked: boolean;
}

export class ProcessSimulationEngine {
  public devices = new Map<string, VirtualDevice>();
  public products: Product[] = [];

  public loadDevices(projectDevices: readonly Device[], projectPipes: readonly PipeConnection[] = []) {
    this.devices.clear();

    const autoTargets = new Map<string, string>();
    const autoSources = new Map<string, string>();

    for (const pipe of projectPipes) {
      const fromDev = projectDevices.find((d) => d.id === pipe.from.deviceId);
      const toDev = projectDevices.find((d) => d.id === pipe.to.deviceId);
      if (!fromDev || !toDev) continue;

      const fromIsOutlet = pipe.from.portType === 'outlet' || pipe.from.portId?.includes('outlet');
      const fromIsInlet = pipe.from.portType === 'inlet' || pipe.from.portId?.includes('inlet');
      const toIsOutlet = pipe.to.portType === 'outlet' || pipe.to.portId?.includes('outlet');
      const toIsInlet = pipe.to.portType === 'inlet' || pipe.to.portId?.includes('inlet');

      if (fromIsOutlet && toIsInlet) {
        if (toDev.type === 'tank') {
          autoTargets.set(fromDev.id, toDev.id);
        }
        if (fromDev.type === 'tank') {
          autoSources.set(toDev.id, fromDev.id);
        }
      } else if (fromIsInlet && toIsOutlet) {
        if (fromDev.type === 'tank') {
          autoTargets.set(toDev.id, fromDev.id);
        }
        if (toDev.type === 'tank') {
          autoSources.set(fromDev.id, toDev.id);
        }
      } else {
        if (toDev.type === 'tank') {
          autoTargets.set(fromDev.id, toDev.id);
        } else if (fromDev.type === 'tank') {
          autoSources.set(toDev.id, fromDev.id);
        }
      }
    }

    for (const d of projectDevices) {
      let Factory = DEVICE_FACTORIES[d.type as keyof typeof DEVICE_FACTORIES];
      if (d.type === 'motor' && d.props['variant'] === 'pump_motor') {
        Factory = DEVICE_FACTORIES['pump'];
      }

      if (Factory) {
        const props = { ...d.props };
        if (!props['targetId'] && autoTargets.has(d.id)) {
          props['targetId'] = autoTargets.get(d.id)!;
        }
        if (!props['sourceId'] && autoSources.has(d.id)) {
          props['sourceId'] = autoSources.get(d.id)!;
        }
        if (props['sourceId'] && props['sourceId'] === props['targetId']) {
          props['sourceId'] = '';
        }

        const vd = new Factory(d.id, d.type, d.name, props, d.transform);
        this.devices.set(vd.id, vd);
      } else {
        console.warn(`Unsupported device type: ${d.type}`);
      }
    }
  }

  public getDevice(id: string): VirtualDevice | undefined {
    return this.devices.get(id);
  }

  public reset() {
    for (const device of this.devices.values()) {
      device.reset();
    }
    this.products = [];
  }

  public stop() {
    this.reset();
  }

  public tick(simulationTimeMs: number, deltaTimeMs: number, host: RuntimeHost, alarms?: AlarmManager) {
    const context = { simulationTimeMs, deltaTimeMs, host, process: this, alarms } as unknown as SimulationContext;
    
    // 0. Pre-update: reset tank flows
    for (const device of this.devices.values()) {
      if (device.type === 'tank') {
        device.state.inletFlowRate = 0;
        device.state.outletFlowRate = 0;
      }
    }

    // 0.5. Process PID controllers first
    for (const device of this.devices.values()) {
      if (device.type === 'pid-controller') {
        device.update(context);
      }
    }

    // 1. Active component updates (Pumps, Valves, Motors, etc.)
    for (const device of this.devices.values()) {
      if (device.type !== 'sensor' && device.type !== 'jam-sensor' && device.type !== 'level-sensor' && device.type !== 'tank' && device.type !== 'pid-controller') {
         device.update(context);
      }
    }

    // 2. Process accumulation components (Tanks)
    for (const device of this.devices.values()) {
      if (device.type === 'tank') {
        device.update(context);
      }
    }

    // 3. Sensors
    for (const device of this.devices.values()) {
      if (device.type === 'sensor' || device.type === 'jam-sensor' || device.type === 'level-sensor') {
        device.update(context);
      }
    }

    // Prepare arrays for product logic
    const conveyors = Array.from(this.devices.values()).filter(d => d.type === 'conveyor');
    
    // Assign products to conveyors if they are not already (AABB check)
    for (const p of this.products) {
      if (!p.active) continue;
      if (!p.conveyorId) {
        for (const conv of conveyors) {
          const cx = conv.transform.position.x;
          const cy = conv.transform.position.y;
          const cw = Number(conv.props['length'] || 400);
          const ch = Number(conv.props['width'] || 60);
          if (p.x + p.width > cx && p.x < cx + cw && p.y + p.height > cy && p.y < cy + ch) {
            p.conveyorId = conv.id;
            break;
          }
        }
      }
    }

    // Process each conveyor independently for physics
    for (const conv of conveyors) {
      const cx = conv.transform.position.x;
      const cw = Number(conv.props['length'] || 400);
      const dirStr = conv.props['direction'] === 'LEFT' ? 'LEFT' : 'RIGHT';
      const speed = Number(conv.state.running ? (conv.state.speed ?? 0) : 0);
      const minSpacing = Number(conv.props['minimumProductSpacing'] || 20);
      const jamThreshold = Number(conv.props['jamThresholdMs'] || 3000);
      const outputConvId = conv.props['outputConnection'] as string;

      // Find products on this conveyor
      const convProducts = this.products.filter(p => p.active && p.conveyorId === conv.id);
      
      // Sort front to back
      convProducts.sort((a, b) => {
        if (dirStr === 'RIGHT') return b.x - a.x; // largest X first
        return a.x - b.x; // smallest X first
      });

      let jammed = false;

      // Update positions and spacing
      for (let i = 0; i < convProducts.length; i++) {
        const p = convProducts[i];
        if (!p) continue;
        const dir = dirStr === 'RIGHT' ? 1 : -1;
        const desiredMove = speed * (deltaTimeMs / 1000);
        let actualMove = desiredMove;
        
        let blockedByFront = false;
        
        if (i > 0) {
          // Check collision with the product in front
          const frontP = convProducts[i - 1];
          if (!frontP || !frontP.active) continue;
          if (dirStr === 'RIGHT') {
            const maxAllowedX = frontP.x - p.width - minSpacing;
            const desiredX = p.x + desiredMove;
            if (desiredX > maxAllowedX) {
              actualMove = Math.max(0, maxAllowedX - p.x);
              blockedByFront = true;
            }
          } else {
            const minAllowedX = frontP.x + frontP.width + minSpacing;
            const desiredX = p.x - desiredMove;
            if (desiredX < minAllowedX) {
              actualMove = Math.max(0, p.x - minAllowedX);
              blockedByFront = true;
            }
          }
        } else {
          // Front product checking end of conveyor
          if (dirStr === 'RIGHT') {
            const endX = cx + cw;
            const desiredX = p.x + desiredMove;
            if (desiredX + p.width > endX) {
               // Reached end. Can we transfer?
               if (outputConvId) {
                  const targetConv = this.getDevice(outputConvId);
                  if (targetConv) {
                     // Check space on target conveyor entry
                     const targetProducts = this.products.filter(tp => tp.active && tp.conveyorId === targetConv.id);
                     let targetHasSpace = true;
                     const targetDir = targetConv.props['direction'] === 'LEFT' ? 'LEFT' : 'RIGHT';
                     const tCx = targetConv.transform.position.x;
                     const targetMinSpacing = Number(targetConv.props['minimumProductSpacing'] || 20);
                     
                     for (const tp of targetProducts) {
                       if (targetDir === 'RIGHT') {
                         if (tp.x < tCx + p.width + targetMinSpacing) targetHasSpace = false;
                       } else {
                         const tEnd = tCx + Number(targetConv.props['length'] || 400);
                         if (tp.x + tp.width > tEnd - p.width - targetMinSpacing) targetHasSpace = false;
                       }
                     }

                     if (targetHasSpace) {
                        // Transfer
                        p.conveyorId = targetConv.id;
                        p.y = targetConv.transform.position.y + (Number(targetConv.props['width']||60) - p.height)/2;
                        if (targetDir === 'RIGHT') {
                          p.x = tCx;
                        } else {
                          p.x = tCx + Number(targetConv.props['length'] || 400) - p.width;
                        }
                     } else {
                        // Blocked by target
                        actualMove = Math.max(0, endX - p.width - p.x);
                        blockedByFront = true;
                     }
                  } else {
                     // Invalid target, drops off
                     p.active = false;
                  }
               } else {
                  // No output conveyor connected
                  const endAction = (conv.props['endAction'] as string) || 'DROP_OFF';
                  if (endAction === 'STOPPER') {
                    actualMove = Math.max(0, endX - p.width - p.x);
                    blockedByFront = true;
                  } else {
                    // DROP_OFF: Allow box to slide off edge into collection bin
                    if (desiredX + p.width / 2 >= endX) {
                      p.active = false;
                    }
                  }
               }
            }
          } else {
            const endX = cx;
            const desiredX = p.x - desiredMove;
            if (desiredX < endX) {
               if (outputConvId) {
                  const targetConv = this.getDevice(outputConvId);
                  if (targetConv) {
                     // Simplified target space check (same logic)
                     const targetProducts = this.products.filter(tp => tp.active && tp.conveyorId === targetConv.id);
                     let targetHasSpace = true;
                     const targetDir = targetConv.props['direction'] === 'LEFT' ? 'LEFT' : 'RIGHT';
                     const tCx = targetConv.transform.position.x;
                     const targetMinSpacing = Number(targetConv.props['minimumProductSpacing'] || 20);
                     for (const tp of targetProducts) {
                       if (targetDir === 'RIGHT') {
                         if (tp.x < tCx + p.width + targetMinSpacing) targetHasSpace = false;
                       } else {
                         const tEnd = tCx + Number(targetConv.props['length'] || 400);
                         if (tp.x + tp.width > tEnd - p.width - targetMinSpacing) targetHasSpace = false;
                       }
                     }
                     if (targetHasSpace) {
                        p.conveyorId = targetConv.id;
                        p.y = targetConv.transform.position.y + (Number(targetConv.props['width']||60) - p.height)/2;
                        if (targetDir === 'RIGHT') p.x = tCx;
                        else p.x = tCx + Number(targetConv.props['length'] || 400) - p.width;
                     } else {
                        actualMove = Math.max(0, p.x - endX);
                        blockedByFront = true;
                     }
                  } else {
                     p.active = false;
                  }
               } else {
                  // No output conveyor connected
                  const endAction = (conv.props['endAction'] as string) || 'DROP_OFF';
                  if (endAction === 'STOPPER') {
                    actualMove = Math.max(0, p.x - endX);
                    blockedByFront = true;
                  } else {
                    // DROP_OFF: Allow box to slide off left edge into collection bin
                    if (desiredX + p.width / 2 <= endX) {
                      p.active = false;
                    }
                  }
               }
            }
          }
        }
        
        p.x += dir * actualMove;
        
        if (p.active && conv.state.running && blockedByFront && desiredMove > 0 && actualMove < desiredMove * 0.9) {
          p.blockedTimeMs += deltaTimeMs;
          p.blocked = true;
        } else {
          p.blockedTimeMs = 0;
          p.blocked = false;
        }
        
        if (p.blockedTimeMs > jamThreshold) {
          jammed = true;
        }
      }
      
      conv.state.jammed = jammed;
      
      // Update linked motor overload
      const motorId = conv.props['motorId'] as string;
      if (motorId) {
        const motor = this.getDevice(motorId);
        if (motor && motor.type === 'motor') {
           motor.state['load'] = jammed ? 'OVERLOAD' : 'NORMAL';
        }
      }
    }

    // Fall off bounds
    for (const p of this.products) {
      if (p.x > 3000 || p.x < -1000 || p.y > 3000 || p.y < -1000) {
        p.active = false;
      }
    }
    this.products = this.products.filter(p => p.active);

    // 2. Input devices & Sensors update
    for (const device of this.devices.values()) {
      if (device.type === 'sensor' || device.type === 'jam-sensor') {
         device.update(context);
      }
    }
  }

  public spawnProduct(x: number, y: number, width: number = 40, height: number = 40) {
    if (this.products.length >= 1000) {
      console.warn("Max products limit reached");
      return;
    }
    this.products.push({
      id: `product-${Date.now()}-${Math.floor(Math.random()*1000)}`,
      x, y, width, height, active: true, velocity: 0, blockedTimeMs: 0, blocked: false
    });
  }

  public removeProduct(id: string) {
    const p = this.products.find(prod => prod.id === id);
    if (p) {
      p.active = false;
      const convId = p.conveyorId;
      this.products = this.products.filter(prod => prod.active);

      if (convId) {
        const remainingBlocked = this.products.some(
          prod => prod.active && prod.conveyorId === convId && (prod.blocked || prod.blockedTimeMs > 0)
        );
        if (!remainingBlocked) {
          const conv = this.getDevice(convId);
          if (conv && conv.type === 'conveyor') {
            conv.state.jammed = false;
            const motorId = conv.props['motorId'] as string;
            if (motorId) {
              const motor = this.getDevice(motorId);
              if (motor && motor.type === 'motor') {
                motor.state['load'] = 'NORMAL';
              }
            }
          }
        }
      }
    }
  }

  public clearJams(conveyorId?: string) {
    for (const p of this.products) {
      if (!conveyorId || p.conveyorId === conveyorId) {
        if (p.blocked || p.blockedTimeMs > 0) {
          p.active = false;
        }
      }
    }
    this.products = this.products.filter(prod => prod.active);

    for (const dev of this.devices.values()) {
      if (dev.type === 'conveyor') {
        if (!conveyorId || dev.id === conveyorId) {
          dev.state.jammed = false;
          const motorId = dev.props['motorId'] as string;
          if (motorId) {
            const motor = this.getDevice(motorId);
            if (motor && motor.type === 'motor') {
              motor.state['load'] = 'NORMAL';
            }
          }
        }
      }
    }
  }

  public clearAllProducts() {
    this.products = [];
    for (const dev of this.devices.values()) {
      if (dev.type === 'conveyor') {
        dev.state.jammed = false;
        const motorId = dev.props['motorId'] as string;
        if (motorId) {
          const motor = this.getDevice(motorId);
          if (motor && motor.type === 'motor') {
            motor.state['load'] = 'NORMAL';
          }
        }
      }
    }
  }
}
